/* eslint-disable deprecation/deprecation */
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { ListAndMenuColors } from '../styles/Colors';
import { DefaultFocusIndicator } from '../styles/FocusIndicators';
import { conditional, isAlphabeticChar } from '../utils/Declarative';
import { generateId } from '../utils/DomUtil';
import { findIndex } from '../utils/FindIndex';
import { ElemAttrs } from '../utils/HtmlAttrs';
import { ListAndMenuPadding } from './List/ListStyles';
import { MenuDividerWidget } from './MenuDividerWidget';
import { GenericMenuItemInfo } from './MenuItemInfo';
// eslint-disable-next-line import/no-cycle
import { MenuItemWidget } from './MenuItemWidget';

export { GenericMenuItemInfo };

export interface ListMenuState {
  activeItemIndex: number;
  keyboardFocused: boolean;
}

export interface BaseListMenuProps extends Pick<ElemAttrs, 'aria-label' | 'aria-labelledby'> {
  /** Function that is called when a menu item is selected. It is passed the value string as its only argument */
  onSelect: (value: string) => void;

  /** Function that is called when the menu needs to be closed because it has lost focus, e.g. when clicking away or pressing TAB */
  requestCloseAll: () => void;

  className?: string;

  /** DOM id */
  id?: string;

  /** The index of the menu item that should be highlighted when the menu is opened  */
  /** @deprecated Per ARIA spec, DO NOT USE -- first menu item should be initially focused */
  __INTERNAL__focusIndex?: number;

  /** Style overrides for the root menu element - can be used for setting the width of the menu */
  menuStyle?: React.CSSProperties;

  /** Max height of the menu in px */
  maxHeight?: number;

  /** Function called on Esc key press - defaults to the function provided in the requestCloseAll prop */
  onEscapeKeyUp?: () => void;

  /** Function called on a left-arrow key press - defaults to a no-op */
  onLeftArrowKeyDown?: () => void;

  /** WAI-ARIA role attribute applied to the menu element */
  role?: 'listbox' | 'menu';

  /** Can the menu's submenus automatically position themselves to try to fit on the screen.  This prop is applied to submenus as well */
  subMenuAllowAutoPosition?: boolean;

  /** Autopositions any submenu when the scrolling root has been scrolled.  Dependent on subMenuAllowAutoPosition being true. */
  subMenuShouldAutoPositionOnScrollingRootScroll?: boolean;

  /**
  * The stacking root is the container in which a div element (mount point) will be appended.  The stacking element
  * will then render to the mount point. If no stackingRootId is provided it will default to the DefaultRootContainer from
  * SharedWidgetsConfig which defaults to document.body
  */
  stackingRootId?: string;

  /**
   * The scrolling root is the container that will have scrolling event listeners attached to it by default from scrolling actions.
   * If no defaultScrollingRootId is provided it will default to the DefaultScrollingContainer from ScrollingRootConfig which defaults
   * to document.body. A reason for setting this is so a callback is fired only when the scrolling root is scrolled, instead of the
   * entire body
   */
  scrollingRootId?: string;

  /**
  * Test Id for the menu - will be appended with '-ListMenu' and be propagated down to
  * menu items ('-{MenuItemInfo value}-MenuItem') and sub menus ('-{MenuItemInfo value}-ListMenu')
  */
  testId: string;

  /**
   * VizClient uses z-indexing in several places to order stacking. This prop should be avoided otherwise.
   */
  zIndex?: number;
}

export interface GenericListMenuProps extends BaseListMenuProps {
  /** The contents of the menu as an array of MenuItemInfos */
  menuItemInfos: GenericMenuItemInfo[];
}

export const ListMenuCursorDriftAllowanceMs = 250;

const ListMenuStyleName: string = TypeStyle.style({
  boxSizing: 'border-box',
  color: ListAndMenuColors.Text,
  fontSize: 12,
  fontWeight: 'normal',
  lineHeight: 'normal',
  outline: 'none',
  padding: ListAndMenuPadding,
  position: 'relative',
  $nest: {
    '&:has(*:focus-visible), &:has(* :focus-visible)': DefaultFocusIndicator(),
  },
});

// eslint-disable-next-line deprecation/deprecation
const isMenuItemEnabled = (item: GenericMenuItemInfo) => item && !item.isDivider && !item.disabled && !item.isUnselectable && !item.isGroup;
const stopPropagation = (e: Event) => { e.stopPropagation(); };
export class ListMenuWidget extends React.Component<GenericListMenuProps, ListMenuState> {
  private domRef: React.RefObject<HTMLDivElement> = React.createRef();
  private displayedSubmenuItemIndex?: number;
  private xVelocity: number = 0;
  private oldX: number;

  public constructor(props: GenericListMenuProps) {
    super(props);
    const firstEnabledItemIndex = Math.max(0, props.menuItemInfos.findIndex(isMenuItemEnabled));

    this.state = {
      // eslint-disable-next-line deprecation/deprecation
      activeItemIndex: this.props.__INTERNAL__focusIndex ?? firstEnabledItemIndex,
      keyboardFocused: true
    };
  }

  public componentDidMount(): void {
    this.addStopPropogationListeners();
  }

  // this is only here for filtering and should be removed when we remove the searchbox support --ckovatch 2018/02/08
  public componentDidUpdate(prevProps: GenericListMenuProps): void {
    if (prevProps.menuItemInfos === this.props.menuItemInfos) {
      return;
    }
    // find previously highlighted string, keep if possible, otherwise select the first item
    let highlightedValue: string | undefined = undefined;
    if (this.state.activeItemIndex > -1 && this.state.activeItemIndex < prevProps.menuItemInfos.length) {
      highlightedValue = prevProps.menuItemInfos[this.state.activeItemIndex].keyboardAccessibleValue;
    }

    // find new selection index - either previous text selection, or the start of the list
    const newActiveIndex: number = Math.max(0, findIndex(
      this.props.menuItemInfos,
      (item: GenericMenuItemInfo) => isMenuItemEnabled(item) && item.keyboardAccessibleValue === highlightedValue,
      0, this
    ));

    if (this.state.activeItemIndex !== newActiveIndex) {
      this.setState({
        activeItemIndex: newActiveIndex
      });
    }
  }

  public componentWillUnmount(): void {
    this.removeStopPropogationListeners();
  }

  public selectActive(): void {
    if (this.state.activeItemIndex < 0 || this.state.activeItemIndex >= this.props.menuItemInfos.length) {
      console.warn(`Tried to select a invalid index: ${this.state.activeItemIndex}`);
      return;
    }
    const currentItem = this.props.menuItemInfos[this.state.activeItemIndex];
    if (isMenuItemEnabled(currentItem)) {
      this.props.onSelect(currentItem.value!);
    }
  }

  /**
   * We stop the propogation of scroll events because components like the dropdown have an event listener on their scrolling
   * root to close the dropdown.  We want the listmenu to be able to scroll without triggering the scrolling root scroll event.
   */
  private addStopPropogationListeners(): void {
    this.domRef.current?.addEventListener('scroll', stopPropagation);
    this.domRef.current?.addEventListener('wheel', stopPropagation);
  }

  private removeStopPropogationListeners(): void {
    this.domRef.current?.removeEventListener('scroll', stopPropagation);
    this.domRef.current?.removeEventListener('wheel', stopPropagation);
  }

  private findNextEnabledItemIndex(currentIndex: number, direction: 1 | -1): number {
    const menuItemCount = this.props.menuItemInfos.length;
    for (let i = currentIndex + direction; i !== currentIndex; i += direction) {
      if (i >= menuItemCount) {
        i = 0;
      } else if (i < 0) {
        i = menuItemCount - 1;
      }
      if (i === currentIndex) {
        return currentIndex;
      }
      if (isMenuItemEnabled(this.props.menuItemInfos[i])) {
        return i;
      }
    }
    return currentIndex;
  }

  private findNextMenuItemStartingWithLetter(letter: string): number {
    return findIndex(
      this.props.menuItemInfos,
      (item: GenericMenuItemInfo) => {
        return isMenuItemEnabled(item) && !!item.keyboardAccessibleValue &&
          item.keyboardAccessibleValue.charAt(0).toUpperCase() === letter.charAt(0).toUpperCase();
      },
      this.state.activeItemIndex + 1, this);
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.altKey || e.ctrlKey || e.metaKey) { return; }

    if (isAlphabeticChar(e.key)) {
      const nextIndexOfFirstLetter = this.findNextMenuItemStartingWithLetter(e.key);
      if (nextIndexOfFirstLetter > -1) {
        this.setActiveItemState(() => nextIndexOfFirstLetter, true);
      }
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    const menuItemCount = this.props.menuItemInfos.length;

    switch (e.key) {
      case 'ArrowUp':
        this.setActiveItemState(() => this.findNextEnabledItemIndex(this.state.activeItemIndex, -1), true);
        break;
      case 'ArrowDown':
        this.setActiveItemState(() => this.findNextEnabledItemIndex(this.state.activeItemIndex, 1), true);
        break;
      case 'ArrowLeft':
        if (this.props.onLeftArrowKeyDown) {
          this.props.onLeftArrowKeyDown();
        }
        break;
      case 'Home':
        this.setActiveItemState(() => this.findNextEnabledItemIndex(-1, 1), true);
        break;
      case 'End':
        this.setActiveItemState(() => this.findNextEnabledItemIndex(menuItemCount, -1), true);
        break;
      case ' ':
      case 'Enter':
        this.selectActive();
        break; // let's not scroll the menu or the page
      case 'Escape':
        break; // eat the keyDown event since we'll handle it in keyUp
      default:
        // Do not prevent default or stopPropagation on unhandled key presses
        return;
    }

    e.preventDefault();
    e.stopPropagation();
  }

  private handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      if (this.props.onEscapeKeyUp) {
        this.props.onEscapeKeyUp();
      } else {
        this.props.requestCloseAll();
      }
      e.preventDefault();
      e.stopPropagation();
    }
  }

  private submenuTimeoutId: number;

  private clearSubmenu = (itemIndex: number): void => {
    this.setActiveItemState(() => itemIndex, false);
  }

  private handleItemHover = (itemIndex: number) => {
    const activeItemIndex = this.state.activeItemIndex;
    const activeItemIsShowingSubmenu = activeItemIndex === this.displayedSubmenuItemIndex;

    // TFSID 707410: allow the pointer to stray on the way to a submenu
    if (activeItemIsShowingSubmenu && this.xVelocity >= 2) {
      clearTimeout(this.submenuTimeoutId);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.submenuTimeoutId = window.setTimeout(this.clearSubmenu.bind(this, itemIndex), ListMenuCursorDriftAllowanceMs);
    } else {
      this.clearSubmenu(itemIndex);
    }
  }

  private setActiveItemState(activeItemIndex: () => number, keyboardFocused: boolean): void {
    clearTimeout(this.submenuTimeoutId);
    this.setState(
      () => ({ activeItemIndex: activeItemIndex(), keyboardFocused }),
      () => {
        this.displayedSubmenuItemIndex = undefined;
        if (!this.state.keyboardFocused) {
          // If no individual item is focused, ensure focus is still on the menu so keyboard input is received
          this.domRef.current!.focus();
        }
      }
    );
  }

  private handleItemSelect = (value: string) => {
    this.props.onSelect(value);
  }

  private handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    this.xVelocity = this.oldX !== undefined ? Math.abs(e.pageX - this.oldX) : 0;
    this.oldX = e.pageX;
  }

  private handleSubmenuShown = (itemIndex: number) => {
    this.displayedSubmenuItemIndex = itemIndex;
  }

  private shouldShowIconGutter(): boolean {
    for (let i = 0; i < this.props.menuItemInfos.length; i++) {
      const currItem = this.props.menuItemInfos[i];
      if (typeof currItem.checked !== 'undefined' || currItem.icon) { return true; }
    }
    return false;
  }

  private buildMenuFromMenuItemInfos(): React.ReactElement[] {
    const { menuItemInfos: menuItems } = this.props;

    const showIconGutter = this.shouldShowIconGutter();
    const menuChildren: React.ReactElement[] = [];

    for (let index = 0; index < menuItems.length; index++) {
      const menuItem = menuItems[index];
      if (menuItem.isDivider) {
        menuChildren.push(<MenuDividerWidget key={index} />);
      } else {
        const makeMenuItem = (item: GenericMenuItemInfo) => {
          if (!item.value) {
            throw new Error(`Each non-divider menu item requires a value`);
          }

          return <MenuItemWidget
            annotation={item.annotation}
            checked={item.checked}
            detail={item.detail}
            disabled={item.disabled}
            displayFormatterBoundWithData={item.displayFormatterBoundWithData}
            icon={item.icon}
            isGroupLabel={item.isGroup}
            isHighlighted={!item.disabled && !item.isUnselectable && this.state.activeItemIndex === index}
            isUnselectable={item.isUnselectable}
            itemIndex={index}
            key={index}
            menuItemInfos={item.menuItemInfos}
            onHover={this.handleItemHover}
            onSelect={this.handleItemSelect}
            onSubmenuShown={this.handleSubmenuShown}
            requestCloseAll={this.props.requestCloseAll}
            role={conditional(this.props.role === 'listbox', 'option')}
            scrollingRootId={this.props.scrollingRootId}
            showIconGutter={showIconGutter}
            stackingRootId={this.props.stackingRootId}
            subMenuAllowAutoPosition={this.props.subMenuAllowAutoPosition}
            subMenuMaxHeight={item.subMenuMaxHeight}
            subMenuShouldAutoPositionOnScrollingRootScroll={this.props.subMenuShouldAutoPositionOnScrollingRootScroll}
            subMenuStyle={item.subMenuStyle}
            testId={this.props.testId}
            value={item.value}
            zIndex={this.props.zIndex}
          />;
        };

        // this logic has much in common with similar logic in List::renderItems
        if (menuItem.isGroup) {
          const groupLabelId = generateId();
          const groupIndex = index;
          const groupItems: React.ReactNode[] = [];

          // first add the label element to top of the group
          groupItems.push(
            <div role='presentation' id={groupLabelId} key='title' onMouseEnter={() => this.handleItemHover(groupIndex)}>
              {makeMenuItem(menuItem)}
            </div>
          );
          // then add items into the group until we see a divider or another group label
          for (index++; !!menuItems[index] && !menuItems[index].isDivider && !menuItems[index].isGroup; index++) {
            groupItems.push(makeMenuItem(menuItems[index]));
          }
          index--;

          menuChildren.push(
            <div role='group' aria-labelledby={groupLabelId} key={groupIndex}>
              {groupItems}
            </div>
          );
        } else {
          menuChildren.push(makeMenuItem(menuItem));
        }
      }
    }
    return menuChildren;
  }

  public render(): JSX.Element {
    const style: React.CSSProperties = {
      ...this.props.menuStyle,
      ...conditional<React.CSSProperties>(!!this.props.maxHeight, {
        maxHeight: this.props.maxHeight,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }),
    };

    return (
      <div
        aria-label={this.props['aria-label']}
        aria-labelledby={this.props['aria-labelledby']}
        className={TypeStyle.classes(ListMenuStyleName, this.props.className)}
        data-tb-test-id={`${this.props.testId}-ListMenu`}
        id={this.props.id}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.handleKeyUp}
        onMouseMoveCapture={this.handleMouseMove}
        ref={this.domRef}
        role={this.props.role}
        style={style}
        tabIndex={-1}
      >
        {this.buildMenuFromMenuItemInfos()}
      </div>
    );
  }
}
